import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Register from "./pages/Register";
import CandidateDashboard from "./pages/CandidateDashboard";
import EmployerDashboard from "./pages/EmployerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ForEmployers from "./pages/ForEmployers";
import AuthCallback from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import FloatingWhatsApp from "./components/FloatingWhatsApp";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppShell({ children }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

function AppRoutes() {
  // Detect OAuth callback via URL fragment — runs during render, not useEffect
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<AppShell><Home /></AppShell>} />
      <Route path="/about" element={<AppShell><About /></AppShell>} />
      <Route path="/jobs" element={<AppShell><Jobs /></AppShell>} />
      <Route path="/jobs/:id" element={<AppShell><JobDetail /></AppShell>} />
      <Route path="/services" element={<AppShell><Services /></AppShell>} />
      <Route path="/contact" element={<AppShell><Contact /></AppShell>} />
      <Route path="/for-employers" element={<AppShell><ForEmployers /></AppShell>} />
      <Route path="/login" element={<AppShell><Login /></AppShell>} />
      <Route path="/register" element={<AppShell><Register /></AppShell>} />
      <Route path="/forgot-password" element={<AppShell><ForgotPassword /></AppShell>} />
      <Route path="/reset-password" element={<AppShell><ResetPassword /></AppShell>} />
      <Route path="/candidate/*" element={
        <ProtectedRoute roles={["candidate"]}>
          <AppShell><CandidateDashboard /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/employer/*" element={
        <ProtectedRoute roles={["employer"]}>
          <AppShell><EmployerDashboard /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute roles={["admin", "recruiter"]}>
          <AppShell><AdminDashboard /></AppShell>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
